import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const PATH = "https://fenny.se/filmproduktion-sverige";

const CITY_LINKS = [
  { label: "Stockholm", path: "/filmproduktion/stockholm" },
  { label: "Göteborg", path: "/filmproduktion/goteborg" },
  { label: "Malmö", path: "/filmproduktion/malmo" },
  { label: "Uppsala", path: "/filmproduktion/uppsala" },
  { label: "Linköping", path: "/filmproduktion/linkoping" },
  { label: "Örebro", path: "/filmproduktion/orebro" },
  { label: "Västerås", path: "/filmproduktion/vasteras" },
  { label: "Jönköping", path: "/filmproduktion/jonkoping" },
  { label: "Helsingborg", path: "/filmproduktion/helsingborg" },
  { label: "Umeå", path: "/filmproduktion/umea" },
  { label: "Lund", path: "/filmproduktion/lund" },
];

const FAQ = [
  {
    q: "Levererar Fenny filmproduktion i hela Sverige?",
    a: "Ja. Vi har lokala team i Stockholm, Göteborg och Malmö och reser regelbundet till Uppsala, Linköping, Örebro, Västerås, Jönköping, Helsingborg, Umeå och Lund. För övriga orter bokar vi närmaste team — restid faktureras transparent eller bakas in i offerten.",
  },
  {
    q: "Vad kostar filmproduktion i Sverige 2026?",
    a: "Social-content från 60 000 kr, reklamfilm 15–30 s från 150 000 kr, brand-film 250 000–700 000 kr, kundcase 45 000–260 000 kr. Pulse-abonnemang för löpande leverans från 14 995 kr/mån. Samma prislogik oavsett ort i Sverige.",
  },
  {
    q: "Hur fungerar produktion på distans?",
    a: "Brief, manus och godkännanden sker digitalt. Vi planerar pre-prod via video, sätter team på plats inspelningsdagen och levererar klipp via Frame.io så ni kan följa och kommentera i realtid. 80% av våra svenska kunder är inte fysiskt med på set.",
  },
  {
    q: "Vilka format levererar ni?",
    a: "16:9 för webb, 9:16 för Reels/Shorts/TikTok, 1:1 och 4:5 för paid social, samt longform för konferens och eftertext. En master per kanal ingår — fler versioner offereras transparent.",
  },
  {
    q: "Har ni drönar-tillstånd för hela Sverige?",
    a: "Ja. A1/A2-tillstånd som täcker hela landet, plus zon-tillstånd för Stockholm-CTR, Landvetter och Sturup. Skog, kust, urbant och industri — vi har flugit allt.",
  },
];

export const Route = createFileRoute("/filmproduktion-sverige")({
  head: () => ({
    meta: [
      { title: "Filmproduktion i hela Sverige — fast pris | Fenny" },
      {
        name: "description",
        content:
          "Filmproduktion i hela Sverige med fast pris och 4–6 veckors leverans. Team i Stockholm, Göteborg och Malmö — reser till alla landets städer. Pulse från 14 995 kr/mån.",
      },
      { property: "og:title", content: "Filmproduktion Sverige | Fenny" },
      {
        property: "og:description",
        content:
          "Premiumfilm för svenska företag — abonnemang eller projekt. Lokala team i tre storstäder, leverans i hela landet.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PATH },
    ],
    links: [{ rel: "canonical", href: PATH }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/filmproduktion-sverige", name: "Filmproduktion Sverige — fast pris, leverans i hela landet | Fenny", description: "Filmproduktion i hela Sverige med fast pris och 4–6 veckors leverans. Team i Stockholm, Göteborg och Malmö — reser till alla landets städer. Pulse från 14 995 kr/mån."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Filmproduktion Sverige",
            path: PATH,
            description:
              "Fenny levererar premiumfilm till svenska företag — abonnemang (Pulse) eller projekt (Prime). Lokala team i Stockholm, Göteborg och Malmö, distansproduktion i hela landet.",
            serviceType: "Filmproduktion",
            priceRange: "Från 14 995 kr/mån (Pulse) eller 60 000 kr/projekt",
            areaServed: ["Sweden"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqPage(FAQ)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Filmproduktion", path: "/filmproduktion-pa-abonnemang" },
            { name: "Sverige", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: SwedenHub,
});

function SwedenHub() {
  return (
    <>
      <Section>
        <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Filmproduktion i Sverige
        </div>
        <h1 className="text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
          Filmproduktion i hela Sverige — fast pris, lokala team
        </h1>
        <QuickAnswer
          text="Fenny är ett svenskt filmteam på abonnemang som levererar premiumfilm till företag i hela Sverige. Vi har lokala team i Stockholm, Göteborg och Malmö och producerar regelbundet i Uppsala, Linköping, Örebro, Västerås, Jönköping, Helsingborg, Umeå och Lund — till fast månadspris (Pulse från 14 995 kr/mån) eller fast projektpris (Prime från 60 000 kr)."
        />
      </Section>

      <Section>
        <h2 className="text-3xl font-semibold md:text-4xl">Städer vi producerar i</h2>
        <p className="mt-4 text-foreground/70">
          Klicka på en stad för lokal pris, leveranstid och case. Är ni på en
          ort som inte listas nedan — kontakta oss, vi producerar i hela landet.
        </p>
        <ul className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {CITY_LINKS.map((c) => (
            <li key={c.path}>
              <Link
                to={c.path}
                className="group flex items-center justify-between rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-sm transition hover:border-foreground/30 hover:bg-foreground/[0.06]"
              >
                <span>Filmproduktion {c.label}</span>
                <ArrowRight className="size-4 opacity-50 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
              </Link>
            </li>
          ))}
        </ul>
      </Section>

      <Section>
        <h2 className="text-3xl font-semibold md:text-4xl">Så fungerar nationell leverans</h2>
        <p className="mt-4 text-foreground/70 max-w-3xl">
          För Stockholm, Göteborg och Malmö har vi fasta team som är på plats
          inom 5 arbetsdagar. För övriga orter bokar vi närmaste team och
          inkluderar restid transparent i offerten. Pre-prod, godkännanden,
          klipp och leverans sker digitalt — 80% av våra kunder är inte
          fysiskt med på inspelning utan följer live via Frame.io.
        </p>
      </Section>

      <Section>
        <h2 className="text-3xl font-semibold md:text-4xl">Vanliga frågor</h2>
        <div className="mt-8 space-y-6 max-w-3xl">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/70">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <SignatureCta />
    </>
  );
}
